import type { ApiResponse } from '../types/api';
import { apiClient } from './api';

export interface PartnerRegistrationData {
  companyName: string;
  capabilitiesSummary?: string;
  additionalInfo?: string;
  contactEmail: string;
  contactPhone?: string;
  website?: string;
  country?: string;
  region?: string;
  capabilityStatementUrl: string;
  logoUrl?: string;
  designatedReceiverId?: string;
}

export const registerPartner = async (
  data: PartnerRegistrationData
): Promise<ApiResponse<{ partnerId: string }>> => {
  try {
    const result = await apiClient.post<{ data?: ApiResponse<{ partnerId: string }> }>(
      '/registerPartnerProfile',
      data
    );

    // We expect the backend to return { data: { success: true, partnerId: ... } }
    // Since apiClient returns the HTTP response body, result is the AxiosResponse object.
    // result.data is the JSON body. If the body has a 'data' field, unwrap it.
    if (result?.data) {
      if (result.data.data) {
        return result.data.data as ApiResponse<{ partnerId: string }>;
      }
      return result.data as unknown as ApiResponse<{ partnerId: string }>;
    }

    return result as unknown as ApiResponse<{ partnerId: string }>;
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Failed to register partner profile: ${err.message}`);
  }
};

/**
 * Downloads a partner's capability statement via Cloud Function.
 */
export const downloadPartnerDocument = async (partnerId: string): Promise<Blob> => {
  try {
    // Note: Because this returns a Blob directly, we configure responseType: 'blob'
    const response = await apiClient.get<unknown>(
      `/downloadPartnerDocument?partnerId=${partnerId}`,
      {
        responseType: 'blob',
      }
    );

    // Depending on the exact AxiosInstance configuration and interceptors,
    // the response might be an AxiosResponse (where we need .data) or already unwrapped.
    const blob =
      (response as { data?: unknown }).data !== undefined
        ? (response as { data: unknown }).data
        : response;

    if (!(blob instanceof Blob)) {
      // Fallback: If it is somehow a string or buffer, we can try to wrap it
      return new Blob([blob as BlobPart]);
    }

    return blob as Blob;
  } catch (error: unknown) {
    const err = error as Error;
    throw new Error(`Failed to download partner document: ${err.message}`);
  }
};

export const getPartner = async (partnerId: string): Promise<Record<string, unknown> | null> => {
  const { firestore } = await import('./firebase');
  const { doc, getDoc } = await import('firebase/firestore');
  const partnerRef = doc(firestore, 'partners', partnerId);
  const partnerSnap = await getDoc(partnerRef);
  if (partnerSnap.exists()) {
    return { id: partnerSnap.id, ...partnerSnap.data() };
  }
  return null;
};

export const getAllPartners = async (): Promise<Record<string, unknown>[]> => {
  const { firestore } = await import('./firebase');
  const { collection, getDocs } = await import('firebase/firestore');
  const partnersColRef = collection(firestore, 'partners');
  const partnersSnapshot = await getDocs(partnersColRef);
  return partnersSnapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
};

export const getPartnerRelationship = async (
  clientId: string,
  partnerId: string
): Promise<Record<string, unknown> | null> => {
  const { firestore } = await import('./firebase');
  const { doc, getDoc } = await import('firebase/firestore');
  const relRef = doc(firestore, 'clients', clientId, 'partnerRelationships', partnerId);
  const relSnap = await getDoc(relRef);
  if (relSnap.exists()) {
    return relSnap.data();
  }
  return null;
};

export const getAllPartnerRelationships = async (
  clientId: string
): Promise<Map<string, 'approved' | 'rejected'>> => {
  const { firestore } = await import('./firebase');
  const { collection, getDocs } = await import('firebase/firestore');
  const relsColRef = collection(firestore, 'clients', clientId, 'partnerRelationships');
  const relsSnapshot = await getDocs(relsColRef);
  const relationshipsMap = new Map<string, 'approved' | 'rejected'>();
  for (const d of relsSnapshot.docs) {
    relationshipsMap.set(d.id, d.data().status as 'approved' | 'rejected');
  }
  return relationshipsMap;
};

export const updatePartnerRelationship = async (
  clientId: string,
  partnerId: string,
  status: 'approved' | 'rejected'
): Promise<void> => {
  const { firestore } = await import('./firebase');
  const { doc, setDoc } = await import('firebase/firestore');
  const relRef = doc(firestore, 'clients', clientId, 'partnerRelationships', partnerId);
  await setDoc(relRef, { status, updatedAt: new Date() }, { merge: true });
};

export const clearPartnerRelationship = async (
  clientId: string,
  partnerId: string
): Promise<void> => {
  const { firestore } = await import('./firebase');
  const { doc, deleteDoc } = await import('firebase/firestore');
  const relRef = doc(firestore, 'clients', clientId, 'partnerRelationships', partnerId);
  await deleteDoc(relRef);
};
